<template>
  <div class="starter-card_holder">
    <!-- Full checklist -->
    <div v-if="showFull" class="starter-card getting-started">
      <div class="getting-started_head">
        <div>
          <h2 class="getting-started_title">Getting started</h2>
          <p class="getting-started_subtitle">
            Five quick things to try — a short tour of what AudioScribe can do.
            Each one ticks off on its own as you go.
          </p>
        </div>
        <div class="getting-started_head-right">
          <span class="getting-started_count">
            {{ completedCount }} of {{ totalCount }} done
          </span>
          <!-- Purely decorative now: the count beside it carries the meaning,
               so announcing both would repeat the same thing twice. -->
          <div class="getting-started_dots" aria-hidden="true">
            <span
              v-for="step in steps"
              :key="`dot-${step.step}`"
              class="getting-started_dot"
              :class="{ 'is-complete': step.completed }"
            ></span>
          </div>
          <button
            type="button"
            class="getting-started_dismiss icon-close"
            aria-label="Hide getting started"
            title="Hide getting started"
            @click="onDismiss"
          ></button>
        </div>
      </div>

      <ul class="getting-started_list">
        <li
          v-for="step in steps"
          :key="step.step"
          class="getting-started_item"
          :class="{
            'is-complete': step.completed,
            'is-locked': isLocked(step),
          }"
        >
          <button
            type="button"
            class="getting-started_row"
            :disabled="step.completed || isLocked(step)"
            @click="onStepClick(step)"
          >
            <span class="getting-started_mark" aria-hidden="true">
              <i v-if="step.completed" class="icon-check"></i>
            </span>
            <span class="getting-started_label">
              {{ labelFor(step.step) }}
              <span class="accessibility">
                {{ step.completed ? "completed" : "not completed" }}
              </span>
            </span>
            <span v-if="isLocked(step)" class="getting-started_locked-hint">
              {{ LOCKED_HINT }}
            </span>
            <span v-else-if="!step.completed" class="getting-started_cta">
              {{ ctaFor(step.step) }}
            </span>
          </button>
        </li>
      </ul>

      <!--
        Home's primary action. Kept outside the list because the upload row is
        disabled once complete, and the ~82% of users who have already uploaded
        would otherwise lose the Import button this page has always had.
      -->
      <div class="getting-started_actions">
        <button type="button" class="btn" @click="openImport">
          Import a file
        </button>
      </div>
    </div>

    <!--
      Collapsed: everything done, or the user hid the card. It still names
      itself and says what re-opening does — a bare "5 of 5" on an otherwise
      empty home page does not tell you what you are looking at.
    -->
    <div v-else-if="loaded" class="starter-card getting-started-collapsed">
      <div class="getting-started-collapsed_main">
        <i
          v-if="allComplete"
          class="icon-check getting-started-collapsed_check"
          aria-hidden="true"
        ></i>
        <div>
          <p class="getting-started-collapsed_title">Getting started</p>
          <p class="getting-started-collapsed_sub">
            <template v-if="allComplete">
              You've tried all {{ totalCount }} — you're set up.
            </template>
            <template v-else>
              {{ completedCount }} of {{ totalCount }} done.
            </template>
            <button
              type="button"
              class="getting-started-collapsed_link"
              @click="expanded = true"
            >
              Show the list
            </button>
          </p>
        </div>
      </div>
      <button type="button" class="btn" @click="openImport">
        Import a file
      </button>
    </div>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import { useAppStore } from "@/stores/app-store";
import { MOBILE_BREAKPOINT_QUERY } from "@/composables/useIsMobile";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import {
  ONBOARDING_STEPS,
  useOnboardingChecklistStore,
} from "@/stores/onboarding-checklist-store";
import { analytics } from "@/services/analytics.service";

export const STEP_LABELS = {
  [ONBOARDING_STEPS.WATCH_TOUR]: "Watch a quick tour",
  [ONBOARDING_STEPS.UPLOAD_FILE]: "Upload your first file",
  [ONBOARDING_STEPS.OPEN_SUMMARY]: "Open a transcript & summary",
  [ONBOARDING_STEPS.ASK_AI_CHAT]: "Ask AI Chat a question",
  [ONBOARDING_STEPS.SEARCH]: "Search across your conversations",
};

/**
 * Steps that cannot do anything until the user has at least one file. Opening a
 * transcript, asking AI Chat and searching all operate on uploaded content, so
 * before the first upload each one leads to an empty page — the conversations
 * list reads "No conversations yet." and search suggests fixing your spelling.
 * They stay visible (seeing what is coming is the point of a checklist) but are
 * held until upload_file completes, which leaves exactly one obvious next step.
 *
 * The gate is deliberately the user-wide upload_file value: it is already derived
 * server-side, so it costs nothing and is exactly right for the case that matters,
 * a brand-new account with no content anywhere. It is not workspace-scoped — no
 * per-workspace content count exists on this page without an extra request, and
 * `remainingFiles` is a daily quota, not a count. So a user with files elsewhere
 * can still reach an empty destination (another workspace, or after deleting their
 * last file). That is why both destinations now explain themselves: the
 * conversations list and the search drop-down each offer an import instead of
 * reporting nothing, which is what actually made those states dead ends.
 */
export const FILE_DEPENDENT_STEPS = [
  ONBOARDING_STEPS.OPEN_SUMMARY,
  ONBOARDING_STEPS.ASK_AI_CHAT,
  ONBOARDING_STEPS.SEARCH,
];

export const LOCKED_HINT = "After your first file";

export const STEP_CTAS = {
  [ONBOARDING_STEPS.WATCH_TOUR]: "Watch",
  [ONBOARDING_STEPS.UPLOAD_FILE]: "Import",
  [ONBOARDING_STEPS.OPEN_SUMMARY]: "Open",
  [ONBOARDING_STEPS.ASK_AI_CHAT]: "Try it",
  [ONBOARDING_STEPS.SEARCH]: "Search",
};

export default {
  name: "GetStartedChecklist",
  data() {
    return {
      expanded: false,
      hasTrackedView: false,
      LOCKED_HINT,
    };
  },
  computed: {
    ...mapStores(useAppStore, useWorkSpaceStore, useOnboardingChecklistStore),
    steps() {
      return this.onboardingChecklistStore.steps;
    },
    completedCount() {
      return this.onboardingChecklistStore.completedCount;
    },
    totalCount() {
      return this.onboardingChecklistStore.totalCount;
    },
    allComplete() {
      return this.onboardingChecklistStore.allComplete;
    },
    loaded() {
      return this.onboardingChecklistStore.loaded;
    },
    showFull() {
      if (!this.loaded) return false;
      return this.onboardingChecklistStore.showFullChecklist || this.expanded;
    },
  },
  watch: {
    showFull(isVisible) {
      this.trackViewOnce(isVisible);
    },
    /**
     * A file has just been uploaded. Tick the box straight away rather than
     * refetching: upload_file is derived from AudioFile, and that row is not
     * queryable for a moment after the client finishes (ConversationsList waits
     * 2s for the same reason), so an immediate read returns a stale `false` and
     * leaves the user staring at the step they just completed. The store's
     * merge is monotonic, so the reconciling read cannot undo this.
     */
    "workspaceStore.fileUploaded": {
      // immediate, so a flag already set before this view mounted (upload
      // started from the top bar, then navigated Home) is not missed.
      immediate: true,
      handler(uploaded) {
        if (!uploaded) return;
        this.onboardingChecklistStore.markStepLocally(
          ONBOARDING_STEPS.UPLOAD_FILE,
        );
      },
    },
    // Reconcile on close too: covers anything the flag missed (a recording, or
    // an upload started before this view mounted). Harmless if it races —
    // completion only ever moves forward.
    "appStore.showImportPopup"(isOpen, wasOpen) {
      if (wasOpen && !isOpen) {
        this.onboardingChecklistStore.fetchChecklist();
      }
    },
  },
  async mounted() {
    await this.onboardingChecklistStore.fetchChecklist();
    this.trackViewOnce(this.showFull);
  },
  methods: {
    /**
     * A step is held only while it depends on content the user does not have
     * yet. Reuses the server-derived upload_file value, so no extra request and
     * it unlocks the moment a file lands.
     */
    isLocked(step) {
      if (step.completed) return false;
      if (!FILE_DEPENDENT_STEPS.includes(step.step)) return false;
      return !this.onboardingChecklistStore.isStepComplete(
        ONBOARDING_STEPS.UPLOAD_FILE,
      );
    },
    labelFor(step) {
      return STEP_LABELS[step] ?? step;
    },
    ctaFor(step) {
      return STEP_CTAS[step] ?? "Go";
    },
    trackViewOnce(isVisible) {
      if (!isVisible || this.hasTrackedView) return;
      this.hasTrackedView = true;
      analytics.onboardingChecklistViewed({
        completed_count: this.completedCount,
        total_count: this.totalCount,
      });
    },
    openImport() {
      analytics.homeImportClicked();
      this.appStore.showImportPopup = true;
    },
    onDismiss() {
      this.expanded = false;
      this.onboardingChecklistStore.dismiss();
    },
    goToConversations() {
      const workspaceUUID = this.workspaceStore.activeWorkspaceUUID;
      if (!workspaceUUID) return;
      this.$router.push(`/${workspaceUUID}/conversations`);
    },
    onStepClick(step) {
      if (step.completed || this.isLocked(step)) return;

      switch (step.step) {
        case ONBOARDING_STEPS.WATCH_TOUR:
          this.appStore.showWelcomeVideoPopup = true;
          break;
        case ONBOARDING_STEPS.UPLOAD_FILE:
          this.openImport();
          break;
        // Both of these live inside a transcript, so send the user to pick one.
        case ONBOARDING_STEPS.OPEN_SUMMARY:
        case ONBOARDING_STEPS.ASK_AI_CHAT:
          this.goToConversations();
          break;
        case ONBOARDING_STEPS.SEARCH:
          if (
            typeof window.matchMedia === "function" &&
            window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches
          ) {
            // Mobile shell: there is no always-mounted top-bar search field;
            // open the full-screen search overlay instead.
            this.appStore.showMobileSearchOverlay = true;
          } else {
            // The desktop top bar is always mounted above this view.
            document.getElementById("searchField")?.focus();
          }
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.getting-started_head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.getting-started_head-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.getting-started_title {
  margin-bottom: 2px;
}

.getting-started_subtitle {
  margin-bottom: 16px;
  opacity: 0.7;
}

.getting-started_count {
  white-space: nowrap;
  opacity: 0.7;
  font-size: 13px;
}

.getting-started_dots {
  display: flex;
  gap: 5px;
}

.getting-started_dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(13, 42, 58, 0.2);

  &.is-complete {
    background: #e15511;
  }
}

.getting-started_dismiss {
  background: none;
  border: 0;
  padding: 4px;
  cursor: pointer;
  opacity: 0.45;
  font-size: 11px;
  line-height: 1;

  &:hover {
    opacity: 0.9;
  }
}

.getting-started_list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.getting-started_item {
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.getting-started_row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: 1px solid rgba(13, 42, 58, 0.15);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #e15511;
  }

  &:disabled {
    cursor: default;
    opacity: 0.65;
  }
}

.getting-started_mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid rgba(13, 42, 58, 0.3);
  font-size: 9px;

  .is-complete & {
    background: #e15511;
    border-color: #e15511;
    color: #fff;
  }
}

.getting-started_label {
  flex: 1;

  .is-complete & {
    text-decoration: line-through;
  }
}

.getting-started_cta {
  flex-shrink: 0;
  font-weight: 600;
  color: #e15511;
}

/*
  Muted with an explicit colour rather than opacity: the hint is essential
  instruction, and stacking its opacity on the row's dropped it to ~39%, well
  under the contrast floor. #5a6b75 on the white card is ~5.5:1, so it reads.
*/
.getting-started_locked-hint {
  flex-shrink: 0;
  font-size: 13px;
  color: #5a6b75;
}

.getting-started_item.is-locked {
  .getting-started_row {
    border-style: dashed;
  }

  /*
    Locked rows are disabled, so the shared :disabled rule above would fade the
    whole row to 0.65 and drag the hint back under the contrast floor. Completed
    rows still fade — there the text is struck through and no longer essential.
  */
  .getting-started_row:disabled {
    opacity: 1;
  }

  .getting-started_label {
    color: #5a6b75;
  }

  /* Decorative only, so this one may safely fade. */
  .getting-started_mark {
    opacity: 0.5;
  }
}

.getting-started_actions {
  margin-top: 16px;
}

.getting-started-collapsed {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.getting-started-collapsed_main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.getting-started-collapsed_title {
  margin: 0;
  font-weight: 600;
}

.getting-started-collapsed_sub {
  margin: 0;
  opacity: 0.7;
}

.getting-started-collapsed_check {
  color: #e15511;
}

.getting-started-collapsed_link {
  background: none;
  border: 0;
  padding: 0;
  margin-left: 4px;
  color: #e15511;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}
</style>
